/*
 * Вход в шапке сайта: «Войти» или «Мой аккаунт».
 *
 * Сайт и кабинет — разные хосты, и ключи сессии кабинета сайту недоступны и не
 * должны быть доступны. Но показывать «Войти» человеку, который вошёл час
 * назад, — значит каждый раз сообщать ему, что аккаунт потерялся.
 *
 * Кабинет ставит рядом с ключами простую метку `bb-account=1` на общем домене.
 * Она ничего не доказывает и ничего не содержит: доступ по-прежнему проверяет
 * только кабинет, и человек с подделанной меткой попадёт на страницу входа.
 * Здесь она решает ровно один вопрос — какое слово написать на кнопке.
 *
 * Метка живёт тридцать дней, как и сама сессия. Если она устарела, кнопка
 * ведёт в кабинет, а кабинет отправляет на вход — то же самое, что было бы и
 * без метки.
 */

const ACCOUNT_HINT = "bb-account";

export function hasAccountHint(cookie: string): boolean {
  return cookie
    .split(";")
    .map((part) => part.trim())
    .some((part) => part === `${ACCOUNT_HINT}=1`);
}

/*
 * Подпись переводится общим каталогом сайта, как и всё остальное. Источник —
 * русская строка: тот же порядок, что у любой другой надписи в разметке.
 */
function label(view: Window, signedIn: boolean): string {
  const source = signedIn ? "Мой аккаунт" : "Войти";
  const i18n = (view as unknown as { BBI18n?: { t?: (value: string) => string } }).BBI18n;
  const translated = i18n?.t?.(source);
  return typeof translated === "string" && translated ? translated : source;
}

/*
 * Конструктор берётся из переданного окна, а не из глобального имени: модуль
 * должен одинаково работать и на странице, и там, где окно подставлено.
 */
function signinAnchor(view: Window): HTMLAnchorElement | null {
  const constructor = (view as unknown as { HTMLAnchorElement?: unknown }).HTMLAnchorElement;
  const node = view.document.getElementById("signin");
  return typeof constructor === "function" && node instanceof (constructor as typeof HTMLAnchorElement)
    ? node
    : null;
}

export function installAccountEntry(view: Window): void {
  const doc = view.document;
  const anchor = signinAnchor(view);
  if (!anchor) return;

  const apply = (): void => {
    let signedIn = false;
    try {
      signedIn = hasAccountHint(doc.cookie);
    } catch {
      // Доступ к cookie может быть закрыт настройками браузера: тогда обычная
      // кнопка входа, а не пустая шапка.
      signedIn = false;
    }
    let target: URL;
    try {
      target = new URL(anchor.href);
    } catch {
      return;
    }
    // Происхождение не трогаем: его выбирает региональная маршрутизация.
    const path = signedIn ? "/account" : "/";
    if (target.pathname !== path) {
      target.pathname = path;
      anchor.href = target.href;
    }
    anchor.textContent = label(view, signedIn);
    anchor.dataset.accountEntry = signedIn ? "account" : "signin";
  };

  apply();
  /*
   * Смена языка перерисовывает шапку целиком и возвращает слово «Войти».
   * Событие приходит после этой перерисовки, поэтому подпись ставится заново.
   */
  view.addEventListener("bb:localechange", apply);
  // Возврат из кабинета кнопкой «назад» показывает страницу из кеша: состояние
  // входа к этому моменту уже другое.
  view.addEventListener("pageshow", apply);
}
